import { useCallback, useEffect, useState } from "react";

export const PRIMARY_WEB_ORIGIN = "https://www.navicareservices.in";
export const PRIMARY_APP_URL = `${PRIMARY_WEB_ORIGIN}/dashboard`;
const LEGACY_ORIGINS = new Set(["naviwash.up.railway.app"]);

/*
 * Shared PWA install state. Capture beforeinstallprompt once at module load so
 * dashboard cards, the mobile nudge and the chatbot all share the same event.
 */
let deferredPrompt = null;
const INSTALLED_KEY = "nw_app_installed";
const SNOOZE_KEY = "nw_install_nudge_until";
const lsGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };
let installedFlag = lsGet(INSTALLED_KEY) === "1";
const subscribers = new Set();
const notify = () => subscribers.forEach((fn) => fn());

export function isPrimaryWebOrigin() {
  if (typeof window === "undefined") return true;
  return window.location.origin === PRIMARY_WEB_ORIGIN;
}

export function redirectLegacyWebOrigin() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  const shouldRedirect = LEGACY_ORIGINS.has(host) || host === "navicareservices.in";
  if (!shouldRedirect) return false;
  const target = `${PRIMARY_WEB_ORIGIN}${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.href !== target) {
    window.location.replace(target);
    return true;
  }
  return false;
}

if (typeof window !== "undefined") {
  // Migrate users who still open the old Railway URL or apex domain.
  redirectLegacyWebOrigin();

  window.addEventListener("beforeinstallprompt", (e) => {
    // Never allow an old-origin prompt to be captured.
    if (!isPrimaryWebOrigin()) return;
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installedFlag = true;
    lsSet(INSTALLED_KEY, "1");
    notify();
  });
  try {
    navigator.getInstalledRelatedApps?.().then((apps) => {
      if (apps && apps.length) {
        installedFlag = true;
        lsSet(INSTALLED_KEY, "1");
        notify();
      }
    }).catch(() => {});
  } catch {}
  if (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true) {
    installedFlag = true;
    lsSet(INSTALLED_KEY, "1");
  }
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return !!(
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.matchMedia?.("(display-mode: fullscreen)")?.matches ||
    window.navigator.standalone === true ||
    installedFlag
  );
}

export function useInstallApp() {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }, []);

  const install = useCallback(async () => {
    if (!isPrimaryWebOrigin()) {
      redirectLegacyWebOrigin();
      return "unavailable";
    }
    if (!deferredPrompt) return "unavailable";
    const evt = deferredPrompt;
    deferredPrompt = null;
    notify();
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      if (choice?.outcome === "accepted") {
        installedFlag = true;
        lsSet(INSTALLED_KEY, "1");
      }
      notify();
      return choice?.outcome || "dismissed";
    } catch {
      return "dismissed";
    }
  }, []);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return {
    canPrompt: !!deferredPrompt && isPrimaryWebOrigin(),
    installed: isStandalone(),
    ios: /iPhone|iPad|iPod/i.test(ua),
    android: /Android/i.test(ua),
    install,
    appUrl: PRIMARY_APP_URL,
  };
}

export function installPlatform() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /iPhone|iPad|iPod/i.test(ua) ? "ios" : /Android/i.test(ua) ? "android" : "desktop";
}
export function isMobileDevice() { return installPlatform() !== "desktop"; }
export function nudgeSnoozed() { const t = Number(lsGet(SNOOZE_KEY) || 0); return t > Date.now(); }
export function snoozeNudge(hours = 4) { lsSet(SNOOZE_KEY, String(Date.now() + hours * 3600 * 1000)); }
